import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from './input';
import { Label } from './label';
import { Textarea } from './textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Progress } from './progress';
import { cn } from '../../lib/utils';
import { 
  AlertTriangle, 
  CheckCircle, 
  Eye, 
  EyeOff, 
  Loader2,
  Info
} from 'lucide-react';

/**
 * Enhanced Input component with validation
 */
export const ValidatedInput = forwardRef(({
  id,
  label,
  value,
  onChange,
  onBlur,
  onFocus,
  error,
  isValid = true,
  required = false,
  disabled = false,
  placeholder = "",
  type = "text",
  icon: Icon = null,
  helpText = "",
  showValidIcon = true,
  isValidating = false,
  className = "",
  labelClassName = "",
  inputClassName = "",
  ...props
}, ref) => {
  const hasError = !!error;
  const showSuccess = isValid && value && showValidIcon && !isValidating;

  return (
    <div className={cn("space-y-2", className)}>
      {/* Label */}
      {label && (
        <Label 
          htmlFor={id} 
          className={cn(
            required && "after:content-['*'] after:text-red-500 after:ml-1",
            labelClassName
          )}
        >
          {label}
        </Label>
      )}

      {/* Input with icons */}
      <div className="relative">
        {/* Left icon */}
        {Icon && (
          <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        )}
        
        {/* Input field */}
        <Input
          ref={ref}
          id={id}
          type={type}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={disabled}
          placeholder={placeholder}
          className={cn(
            Icon && 'pl-10',
            (showSuccess || hasError || isValidating) && 'pr-10',
            hasError && 'border-red-500 focus:border-red-500 focus:ring-red-500',
            showSuccess && 'border-green-500 focus:border-green-500',
            inputClassName
          )}
          {...props}
        />

        {/* Right status icons */}
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          {isValidating && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
          {!isValidating && hasError && (
            <AlertTriangle className="h-4 w-4 text-red-500" />
          )}
          {!isValidating && showSuccess && (
            <CheckCircle className="h-4 w-4 text-green-500" />
          )}
        </div>
      </div>

      {/* Error message */}
      {hasError && (
        <p className="text-sm text-red-600 flex items-center space-x-1">
          <AlertTriangle className="h-4 w-4" />
          <span>{error}</span>
        </p>
      )}

      {/* Help text */}
      {helpText && !hasError && (
        <p className="text-sm text-muted-foreground flex items-center space-x-1">
          <Info className="h-4 w-4" />
          <span>{helpText}</span>
        </p>
      )}
    </div>
  );
});

ValidatedInput.displayName = "ValidatedInput";

/**
 * Password Input with strength indicator
 */
export const PasswordInput = forwardRef(({
  id,
  label,
  value,
  onChange,
  onBlur,
  onFocus,
  error,
  strength,
  required = false,
  disabled = false,
  placeholder,
  showStrength = true,
  showToggle = true,
  helpText = "",
  className = "",
  ...props
}, ref) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = React.useState(false);
  const hasError = !!error;
  const resolvedLabel = label ?? t('validation.passwordLabel');
  const resolvedPlaceholder = placeholder ?? t('validation.passwordPlaceholder');

  const getStrengthColor = (strength) => {
    switch (strength?.color) {
      case 'red': return 'bg-red-500';
      case 'orange': return 'bg-orange-500';
      case 'yellow': return 'bg-yellow-500';
      case 'green': return 'bg-green-500';
      default: return 'bg-gray-300';
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      {/* Label */}
      {resolvedLabel && (
        <Label 
          htmlFor={id} 
          className={required ? "after:content-['*'] after:text-red-500 after:ml-1" : ""}
        >
          {resolvedLabel}
        </Label>
      )}

      {/* Password input */}
      <div className="relative">
        <Input
          ref={ref}
          id={id}
          type={showPassword ? "text" : "password"}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={disabled}
          placeholder={resolvedPlaceholder}
          className={cn(
            showToggle && 'pr-10',
            hasError && 'border-red-500 focus:border-red-500 focus:ring-red-500'
          )}
          {...props}
        />

        {/* Password toggle */}
        {showToggle && (
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Eye className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        )}
      </div>

      {/* Password strength indicator */}
      {showStrength && value && (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{t('validation.passwordStrength')}</span>
            <span className={cn(
              "text-xs font-medium",
              strength?.color === 'red' && "text-red-600",
              strength?.color === 'orange' && "text-orange-600",
              strength?.color === 'yellow' && "text-yellow-600",
              strength?.color === 'green' && "text-green-600"
            )}>
              {strength?.label || t('validation.pwNone')}
            </span>
          </div>
          <Progress 
            value={strength?.strength || 0} 
            className="h-2"
            indicatorClassName={getStrengthColor(strength)}
          />
        </div>
      )}

      {/* Error message */}
      {hasError && (
        <p className="text-sm text-red-600 flex items-center space-x-1">
          <AlertTriangle className="h-4 w-4" />
          <span>{error}</span>
        </p>
      )}

      {/* Help text */}
      {helpText && !hasError && (
        <p className="text-sm text-muted-foreground flex items-center space-x-1">
          <Info className="h-4 w-4" />
          <span>{helpText}</span>
        </p>
      )}
    </div>
  );
});

PasswordInput.displayName = "PasswordInput";

/**
 * Enhanced Textarea with validation
 */
export const ValidatedTextarea = forwardRef(({
  id,
  label,
  value,
  onChange,
  onBlur,
  onFocus,
  error,
  isValid = true,
  required = false,
  disabled = false,
  placeholder = "",
  rows = 3,
  maxLength,
  helpText = "",
  showValidIcon = false,
  isValidating = false,
  className = "",
  labelClassName = "",
  textareaClassName = "",
  ...props
}, ref) => {
  const hasError = !!error;
  const showSuccess = isValid && value && showValidIcon && !isValidating;
  const remainingChars = maxLength ? maxLength - (value?.length || 0) : null;

  return (
    <div className={cn("space-y-2", className)}>
      {/* Label */}
      {label && (
        <div className="flex items-center justify-between">
          <Label 
            htmlFor={id} 
            className={cn(
              required && "after:content-['*'] after:text-red-500 after:ml-1",
              labelClassName
            )}
          >
            {label}
          </Label>
          {maxLength && (
            <span className={cn(
              "text-xs",
              remainingChars < 0 ? "text-red-500" : "text-muted-foreground"
            )}>
              {value?.length || 0}/{maxLength}
            </span>
          )}
        </div>
      )}

      {/* Textarea with validation styling */}
      <div className="relative">
        <Textarea
          ref={ref}
          id={id}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={disabled}
          placeholder={placeholder}
          rows={rows}
          className={cn(
            hasError && 'border-red-500 focus:border-red-500 focus:ring-red-500',
            showSuccess && 'border-green-500 focus:border-green-500',
            textareaClassName
          )}
          {...props}
        />

        {/* Status indicator */}
        {(showSuccess || hasError || isValidating) && (
          <div className="absolute right-3 top-3">
            {isValidating && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            {!isValidating && hasError && (
              <AlertTriangle className="h-4 w-4 text-red-500" />
            )}
            {!isValidating && showSuccess && (
              <CheckCircle className="h-4 w-4 text-green-500" />
            )}
          </div>
        )}
      </div>

      {/* Error message */}
      {hasError && (
        <p className="text-sm text-red-600 flex items-center space-x-1">
          <AlertTriangle className="h-4 w-4" />
          <span>{error}</span>
        </p>
      )}

      {/* Help text */}
      {helpText && !hasError && (
        <p className="text-sm text-muted-foreground flex items-center space-x-1">
          <Info className="h-4 w-4" />
          <span>{helpText}</span>
        </p>
      )}
    </div>
  );
});

ValidatedTextarea.displayName = "ValidatedTextarea";

/**
 * Enhanced Select with validation
 */
export const ValidatedSelect = ({
  id,
  label,
  value,
  onValueChange,
  onBlur,
  onFocus,
  error,
  isValid = true,
  required = false,
  disabled = false,
  placeholder,
  options = [],
  helpText = "",
  showValidIcon = false,
  isValidating = false,
  className = "",
  labelClassName = "",
  children,
  ...props
}) => {
  const { t } = useTranslation();
  const hasError = !!error;
  const showSuccess = isValid && value && showValidIcon && !isValidating;
  const resolvedPlaceholder = placeholder ?? t('common.selectPlaceholder');

  return (
    <div className={cn("space-y-2", className)}>
      {/* Label */}
      {label && (
        <Label 
          htmlFor={id} 
          className={cn(
            required && "after:content-['*'] after:text-red-500 after:ml-1",
            labelClassName
          )}
        >
          {label}
        </Label>
      )}

      {/* Select with validation styling */}
      <div className="relative">
        <Select 
          value={value} 
          onValueChange={onValueChange}
          disabled={disabled}
          {...props}
        >
          <SelectTrigger 
            id={id}
            className={cn(
              hasError && 'border-red-500 focus:border-red-500 focus:ring-red-500',
              showSuccess && 'border-green-500 focus:border-green-500',
              (showSuccess || hasError || isValidating) && 'pr-12'
            )}
            onBlur={onBlur}
            onFocus={onFocus}
          >
            <SelectValue placeholder={resolvedPlaceholder} />
          </SelectTrigger>
          <SelectContent>
            {children || options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status indicator */}
        {(showSuccess || hasError || isValidating) && (
          <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
            {isValidating && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            {!isValidating && hasError && (
              <AlertTriangle className="h-4 w-4 text-red-500" />
            )}
            {!isValidating && showSuccess && (
              <CheckCircle className="h-4 w-4 text-green-500" />
            )}
          </div>
        )}
      </div>

      {/* Error message */}
      {hasError && (
        <p className="text-sm text-red-600 flex items-center space-x-1">
          <AlertTriangle className="h-4 w-4" />
          <span>{error}</span>
        </p>
      )}

      {/* Help text */}
      {helpText && !hasError && (
        <p className="text-sm text-muted-foreground flex items-center space-x-1">
          <Info className="h-4 w-4" />
          <span>{helpText}</span>
        </p>
      )}
    </div>
  );
};

/**
 * Form group for consistent spacing and layout
 */
export const FormGroup = ({ children, className = "" }) => (
  <div className={cn("space-y-4", className)}>
    {children}
  </div>
);

/**
 * Form section with title and optional description
 */
export const FormSection = ({ 
  title, 
  description, 
  icon: Icon, 
  children, 
  className = "" 
}) => (
  <div className={cn("space-y-4", className)}>
    {(title || description) && (
      <div className="space-y-1">
        {title && (
          <h3 className="text-lg font-medium flex items-center space-x-2">
            {Icon && <Icon className="h-5 w-5" />}
            <span>{title}</span>
          </h3>
        )}
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
    )}
    {children}
  </div>
);

/**
 * Validation summary component
 */
export const ValidationSummary = ({ errors, className = "" }) => {
  const { t } = useTranslation();
  const errorList = Object.values(errors).filter(Boolean);
  
  if (errorList.length === 0) return null;

  return (
    <div className={cn(
      "p-4 bg-red-950/20 border border-red-900/40 rounded-lg space-y-2",
      className
    )}>
      <div className="flex items-center space-x-2">
        <AlertTriangle className="h-5 w-5 text-red-400" />
        <h4 className="font-medium text-red-400">
          {t('validation.summaryErrors', { count: errorList.length })}
        </h4>
      </div>
      <ul className="list-disc list-inside space-y-1 text-sm text-red-400">
        {errorList.map((error, index) => (
          <li key={index}>{error}</li>
        ))}
      </ul>
    </div>
  );
};
